import type { CSSProperties } from 'react'
import { Tooltip } from './Tooltip'
import type { HealthBarBand } from '../../api/health-bar'
import { PATTERN_EXPLAINERS } from '../../lib/pattern-catalog-schemas'

// ── Public surface ──────────────────────────────────────────────────────────
//
// Policy v1.0 §2.3 + §2.4: every manager-facing leave-row surface that
// renders an employee should colour by their outlook band. Red HIGH /
// Amber MEDIUM are visible chips; LOW renders nothing so the queue
// doesn't drown in green confetti — consistent with `<HealthBarCard>`'s
// quiet treatment for clean employees.

export interface HealthBarChipProps {
  /** Health-bar band as returned by the api. Pass `null`/`undefined` to
   *  render nothing — same effect as `band="low"`. */
  band: HealthBarBand | null | undefined
  /** Raw point sum, surfaced in the tooltip. Optional — when missing the
   *  tooltip omits the score line. */
  score?: number
  /** Pattern codes feeding the score. Used to build the tooltip body so
   *  the manager can see *why* the employee is flagged without leaving
   *  the queue. */
  contributingPatterns?: ReadonlyArray<string>
  /** Optional override of the chip size. `sm` is the queue-row default;
   *  `md` is reserved for the LeavePatternsPage row. */
  size?: 'sm' | 'md'
  /** Optional test id override. Defaults to `health-bar-chip`. */
  testId?: string
}

// ── Band → presentation tokens ───────────────────────────────────────────────

interface BandPresentation {
  label: string
  swatch: string
  background: string
  border: string
  foreground: string
}

const PRESENTATION: Record<Exclude<HealthBarBand, 'low'>, BandPresentation> = {
  medium: {
    label: 'Outlook: Amber',
    swatch: 'var(--warning)',
    background: 'var(--warning-bg)',
    border: 'var(--warning-line)',
    foreground: 'var(--warning-fg)',
  },
  high: {
    label: 'Outlook: Red',
    swatch: 'var(--danger)',
    background: 'var(--danger-bg)',
    border: 'var(--danger-line)',
    foreground: 'var(--danger-fg)',
  },
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function humanise(code: string): string {
  const words = code.split('_').filter(Boolean)
  if (words.length === 0) return code
  const first = words[0] ?? ''
  const rest = words.slice(1)
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(' ')
}

function buildTooltip(
  band: 'medium' | 'high',
  score: number | undefined,
  patterns: ReadonlyArray<string>,
): string {
  const lines: string[] = []
  lines.push(
    band === 'high'
      ? 'Multiple patterns active — chat with the employee before approving routine annual'
      : 'A pattern or two — keep an eye on the trend',
  )
  if (typeof score === 'number') {
    lines.push(`Score: ${score}`)
  }
  if (patterns.length > 0) {
    const heads = patterns.slice(0, 3).map((code) => {
      const explainer = PATTERN_EXPLAINERS[code]
      return explainer ? `${humanise(code)} — ${explainer.detects}` : humanise(code)
    })
    lines.push(...heads)
    if (patterns.length > 3) {
      lines.push(`+${patterns.length - 3} more`)
    }
  }
  return lines.join('\n')
}

// ── Component ───────────────────────────────────────────────────────────────

/**
 * Compact health-bar status chip. Quiet for `low`/null — the queue
 * doesn't need a chip on every clean row. Renders a coloured pill with
 * a tooltip listing the contributing patterns when band is medium/high.
 *
 * Reused by:
 *  - `<RequestCard>` (manager queue row, behind `healthBarOnRequestCard`).
 *  - `<LeavePatternsPage>` employee row (sort + display).
 *  - `<EmployeePatternsModal>` header (full score read).
 */
export function HealthBarChip({
  band,
  score,
  contributingPatterns = [],
  size = 'sm',
  testId = 'health-bar-chip',
}: HealthBarChipProps): JSX.Element | null {
  if (band == null || band === 'low') return null

  const tokens = PRESENTATION[band]
  const padding = size === 'sm' ? '2px 8px' : '4px 10px'
  const fontSize = size === 'sm' ? 11 : 12

  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding,
    fontSize,
    fontWeight: 600,
    color: tokens.foreground,
    background: tokens.background,
    border: `1px solid ${tokens.border}`,
    borderRadius: 999,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  }

  const swatchStyle: CSSProperties = {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: tokens.swatch,
  }

  const tooltip = buildTooltip(band, score, contributingPatterns)
  const ariaLabel =
    typeof score === 'number'
      ? `${tokens.label}, score ${score}`
      : tokens.label

  const chip = (
    <span
      data-testid={testId}
      data-band={band}
      role="status"
      aria-label={ariaLabel}
      style={style}
    >
      <span aria-hidden="true" style={swatchStyle} />
      {tokens.label}
      {typeof score === 'number' ? (
        <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>· {score}</span>
      ) : null}
    </span>
  )

  return <Tooltip content={tooltip}>{chip}</Tooltip>
}
